/**
 * TrustGate Data Service
 * Handles all API calls and Firestore queries for TrustGate Console
 * Read-only V1 - no write operations
 */

import { collection, query, orderBy, limit, getDocs, doc, getDoc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { db } from '../../firebase/init';
import type {
  TrustGateEpoch,
  TrustGateRequest,
  TrustGateProofResponse,
  TrustGateMetrics,
  TrustGateActivityItem,
  TrustGateWhoami
} from '../types';

// API Base URL (determined by environment)
const getApiBaseUrl = (): string => {
  // In production, use Firebase Functions URL
  if (import.meta.env.PROD) {
    return 'https://us-central1-trustgate-27ddc.cloudfunctions.net';
  }
  // In development, use emulator or production (no local emulator for TrustGate functions)
  return 'https://us-central1-trustgate-27ddc.cloudfunctions.net';
};

/**
 * Fetch epoch metadata from /systemEpoch endpoint
 */
export async function fetchEpochMetadata(): Promise<TrustGateEpoch> {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/systemEpoch`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch epoch metadata: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch whoami data (auth check)
 */
export async function fetchWhoami(idToken: string): Promise<TrustGateWhoami> {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/whoami`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch whoami: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch public proof by proofId
 */
export async function fetchPublicProof(proofId: string): Promise<TrustGateProofResponse> {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/getPublicProof/${proofId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      return { ok: false, error: 'NOT_FOUND' };
    }
    if (response.status === 410) {
      return { ok: false, error: 'EXPIRED_OR_REVOKED' };
    }
    throw new Error(`Failed to fetch proof: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Helper: Convert Firestore Timestamp to Date
 */
function toDate(timestamp: string | { toDate(): Date; seconds: number } | null | undefined): Date | null {
  if (!timestamp) return null;
  if (typeof timestamp === 'string') return new Date(timestamp);
  if (typeof timestamp === 'object' && timestamp !== null) {
    if ('toDate' in timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    if ('seconds' in timestamp && typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000);
    }
  }
  return null;
}

/**
 * Safe Timestamp Normalizer - Prevents crashes from mixed timestamp formats
 * TrustGate defensive client rule: All external data is untrusted
 */
function toMillis(input: any): number {
  if (!input) return 0;

  // Firestore Timestamp
  if (typeof input.toDate === "function") {
    return input.toDate().getTime();
  }

  // JS Date
  if (input instanceof Date) {
    return input.getTime();
  }

  // ISO string
  if (typeof input === "string") {
    const d = new Date(input);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  }

  // Epoch number
  if (typeof input === "number") {
    return input;
  }

  return 0;
}

/**
 * Helper: Get start of today (UTC)
 */
function getStartOfToday(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
}

/**
 * Fetch request by requestId from Firestore
 */
export async function fetchRequestById(requestId: string): Promise<TrustGateRequest | null> {
  try {
    const docRef = doc(db, 'trustgate_requests', requestId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return { id: docSnap.id, ...docSnap.data() } as TrustGateRequest;
  } catch (error) {
    console.error('[TrustGate] Error fetching request:', error);
    throw error;
  }
}

/**
 * Fetch execution receipt by receiptId from Firestore
 */
export async function fetchReceiptById(receiptId: string): Promise<Record<string, unknown> | null> {
  try {
    const docRef = doc(db, 'receipts', receiptId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return { receiptId: docSnap.id, ...docSnap.data() } as Record<string, unknown>;
  } catch (error) {
    console.error('[TrustGate] Error fetching receipt:', error);
    throw error;
  }
}

/**
 * Fetch recent activity (last 10 requests)
 */
export async function fetchRecentActivity(): Promise<TrustGateActivityItem[]> {
  try {
    const q = query(
      collection(db, 'trustgate_requests'),
      orderBy('createdAt', 'desc'),
      limit(10)
    );

    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data() as TrustGateRequest;
      return {
        id: doc.id,
        status: data.status,
        decision: data.decision,
        amount: data.lockedParams.amount,
        currency: data.lockedParams.currency,
        destination: data.lockedParams.destination,
        createdAt: toDate(data.createdAt) || new Date(),
        requestedBy: data.requestedBy,
        requestedByEmail: data.requestedByEmail,
      };
    });
  } catch (error) {
    console.error('[TrustGate] Error fetching recent activity:', error);
    throw error;
  }
}

/**
 * Fetch activity metrics (KPI tiles)
 */
export async function fetchActivityMetrics(): Promise<TrustGateMetrics> {
  try {
    const startOfToday = getStartOfToday();

    // Query 1: All requests
    const allRequestsQuery = query(collection(db, 'trustgate_requests'));
    const allRequestsSnapshot = await getDocs(allRequestsQuery);
    const allRequests = allRequestsSnapshot.docs.map(d => d.data() as TrustGateRequest);

    // Calculate metrics
    const requestsToday = allRequests.filter(r => {
      const createdAt = toDate(r.createdAt);
      return createdAt && createdAt >= startOfToday;
    }).length;

    const decisionsWithOutcome = allRequests.filter(r => r.decision === 'ALLOW' || r.decision === 'DENY');
    const allowCount = decisionsWithOutcome.filter(r => r.decision === 'ALLOW').length;
    const approvalRate = decisionsWithOutcome.length > 0 
      ? Math.round((allowCount / decisionsWithOutcome.length) * 100) 
      : 0;

    const pendingCount = allRequests.filter(r => r.status === 'PENDING').length;

    const executedToday = allRequests.filter(r => {
      const updatedAt = toDate(r.updatedAt);
      return r.status === 'EXECUTED' && updatedAt && updatedAt >= startOfToday;
    }).length;

    return {
      requestsToday,
      approvalRate,
      pendingCount,
      executedToday,
      loading: false,
      error: null,
    };
  } catch (error) {
    console.error('[TrustGate] Error fetching metrics:', error);
    return {
      requestsToday: 0,
      approvalRate: 0,
      pendingCount: 0,
      executedToday: 0,
      loading: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Format currency amount (cents to dollars)
 */
export function formatCurrency(amountCents: number, currency: string = 'USD'): string {
  const dollars = amountCents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(dollars);
}

/**
 * Format relative time (e.g., "2m ago", "5h ago")
 */
export function formatRelativeTime(date: Date | string | { toDate(): Date } | null | undefined): string {
  if (!date) return '—';
  
  const now = new Date();
  const diffMs = now.getTime() - toMillis(date);
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffDay > 0) return `${diffDay}d ago`;
  if (diffHour > 0) return `${diffHour}h ago`;
  if (diffMin > 0) return `${diffMin}m ago`;
  return 'Just now';
}

/**
 * Format timestamp (ISO string or Firestore Timestamp)
 */
export function formatTimestamp(timestamp: string | { toDate(): Date; seconds: number } | null | undefined): string {
  const date = toDate(timestamp);
  if (!date) return '—';
  
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);
}

// ============================================================
// REAL-TIME LISTENERS (V2)
// ============================================================

/**
 * Subscribe to real-time updates for trustgate_requests
 * 
 * @param callback - Called with updated requests array on each change
 * @param maxItems - Maximum number of items to fetch (default: 20)
 * @returns Unsubscribe function to stop listening
 */
export function subscribeToRequests(
  callback: (requests: TrustGateRequest[]) => void,
  maxItems = 20
): Unsubscribe {
  const requestsQuery = query(
    collection(db, 'trustgate_requests'),
    orderBy('createdAt', 'desc'),
    limit(maxItems)
  );

  const unsubscribe = onSnapshot(
    requestsQuery,
    (snapshot) => {
      const requests = snapshot.docs.map(doc => doc.data() as TrustGateRequest);
      callback(requests);
    },
    (error) => {
      console.error('[TrustGateDataService] Requests listener error:', error);
      // Call callback with empty array on error to prevent stale data
      callback([]);
    }
  );

  return unsubscribe;
}

/**
 * Subscribe to real-time updates for trustgate_proofs
 * 
 * @param callback - Called with updated proofs array on each change
 * @param maxItems - Maximum number of items to fetch (default: 20)
 * @returns Unsubscribe function to stop listening
 */
export function subscribeToProofs(
  callback: (proofs: unknown[]) => void,
  maxItems = 20
): Unsubscribe {
  const proofsQuery = query(
    collection(db, 'trustgate_proofs'),
    orderBy('createdAt', 'desc'),
    limit(maxItems)
  );

  const unsubscribe = onSnapshot(
    proofsQuery,
    (snapshot) => {
      const proofs = snapshot.docs.map(doc => doc.data());
      callback(proofs);
    },
    (error) => {
      console.error('[TrustGateDataService] Proofs listener error:', error);
      // Call callback with empty array on error to prevent stale data
      callback([]);
    }
  );

  return unsubscribe;
}
